import { useEffect, useRef } from "react";
import apiService from "../services/api";

const API_URL = process.env.REACT_APP_API_URL || "https://dev-backend.taliaz-cm.com";

export type NotificationEvent =
    | { type: "tasks_count_changed" }
    | { type: "whatsapp_unread_count"; count: number }
    | { type: "notification_count_changed" }
    /** The assistant or a supporter answered a conversation of this user's. */
    | {
        type: "ai_support_reply";
        session_id: number;
        reason: string;
        title?: string | null;
        by?: string;
        preview?: string;
    }
    /** A conversation needs human support: an approval, or an escalation. */
    | {
        type: "ai_support_attention";
        session_id: number;
        reason: string;
        action?: string;
        action_id?: number;
    };

interface UseNotificationsOptions {
    onTasksCount: (count: number) => void;
    onWhatsAppUnread?: (count: number) => void;
    onNotificationCountChanged?: () => void;
}

export function useNotifications(
    isAuthenticated: boolean,
    options: UseNotificationsOptions
) {
    const { onTasksCount, onWhatsAppUnread, onNotificationCountChanged } = options;
    const esRef = useRef<EventSource | null>(null);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!isAuthenticated) {
            cleanup();
            return;
        }

        connect();
        return cleanup;

        function connect() {
            const token = localStorage.getItem("token");
            if (!token) return;

            const es = new EventSource(`${API_URL}/events?token=${token}`);
            esRef.current = es;

            es.onmessage = (e) => {
                try {
                    const event: NotificationEvent = JSON.parse(e.data);
                    if (event.type === "tasks_count_changed") {
                        const currentToken = localStorage.getItem("token") || "";
                        apiService.getNumberOfTasksNeedAction({ token: currentToken })
                            .then((data) => onTasksCount(data.result))
                            .catch(() => {});
                    } else if (event.type === "whatsapp_unread_count") {
                        onWhatsAppUnread?.(event.count);
                    } else if (event.type === "notification_count_changed") {
                        // Signal only: the notification center refetches its data.
                        onNotificationCountChanged?.();
                    } else if (
                        event.type === "ai_support_reply" ||
                        event.type === "ai_support_attention"
                    ) {
                        // Re-broadcast on the DOM so the AI Support widget and
                        // console can react without opening a second event
                        // stream or depending on provider order.
                        window.dispatchEvent(
                            new CustomEvent(event.type, { detail: event })
                        );
                    }
                } catch {
                    console.error("[Notifications] Failed to parse event:", e.data);
                }
            };

            es.onerror = () => {
                es.close();
                esRef.current = null;
                // Reconnect after 5 seconds
                reconnectTimerRef.current = setTimeout(() => {
                    connect();
                }, 5000);
            };

            console.log("[Notifications] SSE connection opened");
        }

        function cleanup() {
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (esRef.current) {
                esRef.current.close();
                esRef.current = null;
                console.log("[Notifications] SSE connection closed");
            }
        }
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps
}
